import { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { ChatMessage } from 'services/api/chat';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import MessageBubble from './MessageBubble';

type Props = {
  // Oldest→newest (newest pinned to the bottom).
  messages: ChatMessage[];
  currentUserId: string;
  hasOlder: boolean;
  isFetchingOlder: boolean;
  onLoadOlder: () => void;
  // Whether the NEWEST message is actually in the viewport (at/near the bottom of the
  // scroll). ChannelView gates mark-read on it: a message that appended off-screen while
  // the reader was scrolled up has NOT been seen (OLIO-2835).
  onNewestVisibleChange?: (visible: boolean) => void;
};

// Trigger "load older" once the scroll nears the top; treat "within this many px of the
// bottom" as pinned so a newly sent/arrived message auto-scrolls but reading history doesn't.
const TOP_THRESHOLD = 48;
const BOTTOM_THRESHOLD = 64;

// The scrolling message pane: newest pinned at the bottom, "load older" at the top with
// scroll anchoring (hold the reader's position while older messages prepend). The upward
// cursor pagination is new work — no existing infinite list in this repo scrolls up.
//
// A message arriving while the reader is scrolled up deliberately does NOT autoscroll
// (yanking mid-read is hostile) — instead a "New messages" pill floats at the top of the
// pane until the reader returns to the bottom (click = jump there). Own sends never pill.
export default function MessageList({
  messages,
  currentUserId,
  hasOlder,
  isFetchingOlder,
  onLoadOlder,
  onNewestVisibleChange,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Scroll height captured just before an older page is requested — the anchor delta.
  const heightBeforeOlder = useRef<number | null>(null);
  const nearBottom = useRef(true);
  // The newest message id last seen while at the bottom — the pill shows when the newest
  // moves past it while the reader is scrolled up.
  const lastSeenNewestIdRef = useRef<string | null>(null);
  const [showNewMessagesPill, setShowNewMessagesPill] = useState(false);

  const newestId = messages.length ? messages[messages.length - 1].id : null;
  const newestIsOwn = messages.length ? messages[messages.length - 1].createdBy.id === currentUserId : false;

  const syncBottomState = (el: HTMLDivElement) => {
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD;
    nearBottom.current = atBottom;
    if (atBottom && newestId) {
      lastSeenNewestIdRef.current = newestId;
      setShowNewMessagesPill(false);
    }
    onNewestVisibleChange?.(atBottom);
    return atBottom;
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (heightBeforeOlder.current !== null) {
      // Older messages just prepended: keep the previously-visible message under the cursor.
      el.scrollTop = el.scrollHeight - heightBeforeOlder.current;
      heightBeforeOlder.current = null;
    } else if (nearBottom.current) {
      // Initial load, own send, or a message arriving while already at the bottom.
      el.scrollTop = el.scrollHeight;
    }

    const atBottom = syncBottomState(el);
    // A message from someone else landed below the fold — signal it without yanking.
    if (!atBottom && newestId && !newestIsOwn && newestId !== lastSeenNewestIdRef.current) {
      setShowNewMessagesPill(true);
    }
    // syncBottomState reads only refs + the values this effect already keys on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    syncBottomState(el);

    if (el.scrollTop <= TOP_THRESHOLD && hasOlder && !isFetchingOlder && heightBeforeOlder.current === null) {
      heightBeforeOlder.current = el.scrollHeight;
      onLoadOlder();
    }
  };

  const jumpToNewest = () => {
    const el = scrollRef.current;
    if (!el) return;

    if (typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    else el.scrollTop = el.scrollHeight;
    // Smooth scrolling emits scroll events that converge state; jsdom/fallback needs the sync.
    syncBottomState(el);
  };

  return (
    <ListArea>
      {showNewMessagesPill && (
        <NewMessagesPill type='button' data-testid='chat-new-messages-pill' onClick={jumpToNewest}>
          New messages ↓
        </NewMessagesPill>
      )}
      <Scroll ref={scrollRef} onScroll={handleScroll} data-testid='chat-message-list'>
        {isFetchingOlder && <LoadingRow>Loading earlier messages…</LoadingRow>}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} isOwn={message.createdBy.id === currentUserId} />
        ))}
      </Scroll>
    </ListArea>
  );
}

const ListArea = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 8px 4px 16px;
`;

const NewMessagesPill = styled.button`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  border: none;
  cursor: pointer;
  border-radius: 999px;
  padding: 6px 14px;
  background: ${colors.blue100};
  color: ${colors.white};
  font-size: 12px;
  box-shadow: 0 2px 8px rgba(2, 19, 32, 0.2);
`;

const LoadingRow = styled(BodySmall)`
  text-align: center;
  color: ${colors.black50};
  padding: 4px 0;
`;
